/* eslint-disable no-restricted-syntax */
import React, { useState } from "react";
import { Link, useMatches, useRouter } from "@tanstack/react-router";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { appEnv } from "@/constants/env";
import { analytics } from "@/analytics/ga";

export const RouteTestOverlay = observer(() => {
  const {
    activeProvinceId,
    activeLanguage,
    activeIndigenousCommunity,
    setActiveIndigenousCommunity,
    setOrderData,
    orderData,
    isIndigenousFlow,
    isEmergentFlow,
  } = useStore();
  const router = useRouter();
  const activeRoutes = useMatches();
  const activeRoute = activeRoutes[activeRoutes.length - 1];

  const [isOpen, setIsOpen] = useState(appEnv.isDevelopment);
  const [ga4Debug, setGa4Debug] = useState(false);

  const handleToggleGa4Debug = (checked: boolean) => {
    setGa4Debug(checked);
    analytics.setDebug(checked);
  };

  if (!isOpen) return null;

  const handleClick = () => {
    if (!orderData.isActive) setOrderData({ ...orderData, isActive: true });
  };

  const handleToggleIndigenousFlow = (checked: boolean) => {
    if (checked) {
      setActiveIndigenousCommunity({
        id: "test-community",
        name: "Test Community",
      } as any);
    } else {
      setActiveIndigenousCommunity(undefined);
    }
  };

  const handleToggleEmergentFlow = (checked: boolean) => {
    if (checked) {
      // Emergent flow requires ada to be set and indigenous flow to be off
      setActiveIndigenousCommunity(undefined);
      setOrderData({ ...orderData, ada: orderData.ada || "TEST-ADA" });
    } else {
      setOrderData({ ...orderData, ada: "" });
    }
  };

  return (
    <Stack
      color="black"
      position="fixed"
      bottom="0"
      right="0"
      p="5"
      width="320px"
      zIndex="9999"
      display="flex"
      flexDirection="column"
      bg="white"
    >
      <Button
        size="sm"
        onClick={() => setIsOpen(false)}
      >
        Close
      </Button>

      <FormControl
        display="flex"
        alignItems="center"
      >
        <FormLabel width="100px">Ada #</FormLabel>
        <Input
          border="solid 1px grey"
          value={orderData.ada}
          onChange={(e) => setOrderData({ ...orderData, ada: e.target.value })}
        />
      </FormControl>

      <FormControl
        display="flex"
        alignItems="center"
      >
        <FormLabel width="100px">Discount</FormLabel>
        <Switch
          onChange={(e) =>
            setOrderData({
              ...orderData,
              promotionalCode: e.target.checked ? "TEST" : "",
              promotionalDiscountAmount: e.target.checked ? 1 : 0,
            })
          }
          isChecked={!!orderData.promotionalCode}
        />
      </FormControl>

      <FormControl
        display="flex"
        alignItems="center"
      >
        <FormLabel width="140px">GA4 Debug</FormLabel>
        <Switch
          onChange={(e) => handleToggleGa4Debug(e.target.checked)}
          isChecked={ga4Debug}
        />
      </FormControl>

      <FormControl
        display="flex"
        alignItems="center"
      >
        <FormLabel width="140px">Indigenous Flow</FormLabel>
        <Switch
          onChange={(e) => handleToggleIndigenousFlow(e.target.checked)}
          isChecked={isIndigenousFlow}
        />
      </FormControl>

      <FormControl
        display="flex"
        alignItems="center"
      >
        <FormLabel width="140px">Emergent Flow</FormLabel>
        <Switch
          onChange={(e) => handleToggleEmergentFlow(e.target.checked)}
          isChecked={isEmergentFlow}
        />
      </FormControl>

      <Text>IsOrderActive: {orderData.isActive ? "True" : "False"}</Text>
      <Text>Community: {activeIndigenousCommunity?.name}</Text>
      <Text color="primary">Active Province: {activeProvinceId || "None"}</Text>
      <Stack spacing="0">
        {Object.keys(router.routesByPath).map((path) => {
          const route = (router.routesByPath as any)[path] as any;

          if (route.options.component == null) return null;

          return (
            <Box
              key={path}
              color={activeRoute.pathname === path ? "primary" : ""}
            >
              <Link
                to={path}
                onClick={handleClick}
                params={{
                  provinceId: activeProvinceId || "ON",
                }}
                search={
                  {
                    language: activeLanguage,
                  } as any
                }
              >
                {path}
              </Link>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
});
